<template>
    <div v-if="loading">
        {{ loadingMessage }}
    </div>
    <div v-else class="card">
        <div class="card-header">
            <div class="row align-items-center">
                <div class="col-sm-6 col-lg-8">
                    <h4 class="header-title">{{ $t('installationAlerts.listTitle') }}</h4>
                </div>
                <div class="col-sm-6 col-lg-4 mt-2 mt-sm-0">
                    <div class="d-flex">
                        <input type="text" id="searchTrigger" class="form-control form-control-sm" :placeholder="$t('common.search')" aria-controls="users-table" maxlength="64">
                        <button v-if="hasAlerts" type="button" class="btn ms-2 btn-primary text-nowrap" @click="openAcquitModal(0)">
                            <i class="mdi mdi-cancel"></i><span class="d-none d-xxl-inline-block ms-1">{{ $t('installationAlerts.ackAll') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="card-body pt-0">
            <CustomDataTable ref="datatable" tableId="alerts-table" :columns="columns" :data="alerts" :emptyTableLabel="$t('installationAlerts.emptyTable')" :rowClick="rowClick" />
        </div>
    </div>
    <ConfirmationModal ref="confirmModal" :title="$t('installationAlerts.confirmAck')" :question="confirmationText" type="danger" :button1="$t('common.cancel')" :button2="$t('installationAlerts.ack')" :action="acquitAlert" />
</template>

<script>
import { onMounted, ref, computed } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import ConfirmationModal from '@/modals/confirmationModal.vue';
import CustomDataTable from '@/components/customDatatable.vue';

export default {
    name: "AlertList",
    components: {
        ConfirmationModal,
        CustomDataTable,
    },
    setup() {
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.id);
        const userStore = useUserStore();
        const alerts = ref([]);
        const confirmationText = ref('');
        const confirmModal = ref(null);
        const datatable = ref(null);
        const loading = ref(true);
        const loadingMessage = ref(t('common.loading'));

        const columns = [
            {
                id: 0,
                title: t('installationAlerts.columns.alert'),
                data: 'description',
                orderable: true,
                searchable: true,
                visible: true,
                className: 'cell-name',
                render: (data) => {
                    return `
                        <span class="d-flex align-items-center">
                            <i class="mdi mdi-alert text-danger me-2"></i>
                            <span class="fw-bold text-danger">${data}</span>
                        </span>
                    `;
                }
            },
            {
                id: 1,
                title: t('installationAlerts.columns.equipment'),
                data: 'component',
                orderable: true,
                searchable: true,
                visible: true,
                className: 'cell-name',
            },
            {
                id: 2,
                title: t('installationAlerts.columns.date'),
                data: 'timestamp',
                orderable: true,
                searchable: true,
                visible: true,
                className: 'cell-date',
                render: (data, type, row) => {
                    if (type === 'sort' || type === 'type') {
                        return data.getTime();
                    }
                    return row.getAlertDate;
                }
            },
            {
                id: 3,
                title: t('installationAlerts.columns.action'),
                data: null,
                orderable: false,
                searchable: false,
                visible: true,
                className: 'table-action',
                render: (row) => {
                    return `
                        <button class="btn btn-sm btn-outline-danger row-click" row-action='acknowledge' row-id="${row.id}">
                            <i class="mdi mdi-cancel"></i><span class="d-none d-xxl-inline-block ms-1">${t('installationAlerts.ack')}</span>
                        </button>
                    `;
                }
            },
        ];

        onMounted(async () => {
            try {
                alerts.value = await userStore.loadAlerts(facilityId);
                loading.value = false;
            } catch(error) {
                loadingMessage.value = error;
            }
        });

        function rowClick(action, id) {
            switch(action) {
                case 'acknowledge':
                    openAcquitModal(id);
                    break;
            }
        }

        function openAcquitModal(id) {
            if(id == 0) {
                confirmationText.value = t('installationAlerts.confirmAckAll');
            } else {
                confirmationText.value = t('installationAlerts.confirmAckOne');
            }
            confirmModal.value.show(id);
        }

        async function acquitAlert(id) {
            try {
                if(id == 0) {
                    await userStore.ackAllAlerts(facilityId);
                    alerts.value = [];
                    showToast('success', t('installationAlerts.allAcked'));
                } else {
                    await userStore.ackAlert(facilityId, id);
                    alerts.value = alerts.value.filter(a => a.id != id);
                    showToast('success', t('installationAlerts.oneAcked'));
                }
                datatable.value.refresh(alerts.value);
            } catch (e) {
                showToast('error', e);
            }
        }

        const hasAlerts = computed(() => {
            return alerts.value.length > 0;
        });

        return {
            confirmationText,
            openAcquitModal,
            acquitAlert,
            hasAlerts,
            confirmModal,
            datatable,
            rowClick,
            columns,
            loading,
            loadingMessage,
            alerts,
        };
    },
};
</script>
